/**
 * DeployModal — final gate before landing-page deploy.
 *
 *   - Side-by-side diff (byte counts for now; future: char-level diff)
 *   - Rollback snapshot ID display
 *   - 24-hour hard gate: if event starts <24h away, the operator MUST
 *     type the event shortname exactly before the "Deploy live" button
 *     enables
 *   - Dry-run first is the default; operators must untick it to go live
 */

import { useMemo, useState } from 'react'
import { AlertTriangle, ShieldCheck, X } from 'lucide-react'

interface DeployModalProps {
  open: boolean
  onClose: () => void
  onDeploy: (args: {
    dryRun: boolean
    typedConfirmation: string
    eventShortname: string
    operatorComment: string
  }) => Promise<void>
  currentHtml: string
  nextHtml: string
  eventShortname: string
  eventStartIso?: string | null
  rollbackSnapshotId?: string | null
  /** For tests: substitute Date.now() */
  now?: () => number
}

export function DeployModal({
  open,
  onClose,
  onDeploy,
  currentHtml,
  nextHtml,
  eventShortname,
  eventStartIso,
  rollbackSnapshotId,
  now = Date.now,
}: DeployModalProps) {
  const [typedConfirmation, setTypedConfirmation] = useState('')
  const [operatorComment, setOperatorComment] = useState('')
  const [dryRun, setDryRun] = useState(true)
  const [busy, setBusy] = useState(false)

  const { within24h } = useMemo(() => {
    if (!eventStartIso) return { within24h: false }
    const start = Date.parse(eventStartIso)
    if (Number.isNaN(start)) return { within24h: false }
    return { within24h: start > now() && start - now() < 24 * 60 * 60 * 1000 }
  }, [eventStartIso, now])

  if (!open) return null

  const typedOk = typedConfirmation.trim() === eventShortname
  const canDeploy = dryRun || !within24h || typedOk

  async function handleDeploy() {
    if (!canDeploy) return
    setBusy(true)
    try {
      await onDeploy({ dryRun, typedConfirmation: typedConfirmation.trim(), eventShortname, operatorComment: operatorComment.trim() })
    } finally {
      setBusy(false)
    }
  }

  const currentBytes = new Blob([currentHtml]).size
  const nextBytes = new Blob([nextHtml]).size
  const deltaBytes = nextBytes - currentBytes

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="deploy-modal-title"
      data-testid="deploy-modal"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4"
    >
      <div className="w-full max-w-xl rounded-2xl bg-white p-5 shadow-xl dark:bg-slate-800">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 id="deploy-modal-title" className="text-sm font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-200">
              Deploy landing to butler1
            </h3>
            <p className="mt-1 text-xs text-slate-500">Event: {eventShortname}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mb-4 grid grid-cols-2 gap-3">
          <div className="rounded-md border border-slate-200 p-3 dark:border-slate-700">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Current</div>
            <div className="mt-1 text-lg font-semibold text-slate-700 dark:text-slate-100">
              {(currentBytes / 1024).toFixed(1)} KB
            </div>
          </div>
          <div className="rounded-md border border-slate-200 p-3 dark:border-slate-700">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">After deploy</div>
            <div className="mt-1 text-lg font-semibold text-marquee-600">
              {(nextBytes / 1024).toFixed(1)} KB
              <span className={`ml-2 text-xs ${deltaBytes >= 0 ? 'text-rose-500' : 'text-emerald-600'}`}>
                {deltaBytes >= 0 ? '+' : ''}{(deltaBytes / 1024).toFixed(1)} KB
              </span>
            </div>
          </div>
        </div>
        {rollbackSnapshotId && (
          <div className="mb-3 rounded-md bg-slate-50 p-2 text-[11px] font-mono text-slate-600 dark:bg-slate-900 dark:text-slate-300">
            Rollback snapshot id: <span data-testid="rollback-snapshot-id">{rollbackSnapshotId}</span>
          </div>
        )}

        {within24h && !dryRun && (
          <div
            role="alert"
            className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <div className="font-semibold">Event starts within 24 hours</div>
              <div className="mt-1">
                Type <span className="font-mono">{eventShortname}</span> below to authorize a live deploy.
              </div>
              <input
                type="text"
                value={typedConfirmation}
                onChange={(e) => setTypedConfirmation(e.target.value)}
                className="mt-2 w-full rounded border border-amber-300 bg-white px-2 py-1 font-mono text-xs"
                aria-label="Event shortname confirmation"
                placeholder={eventShortname}
                data-testid="typed-confirmation"
              />
            </div>
          </div>
        )}

        <label className="mb-3 flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={dryRun}
            onChange={(e) => setDryRun(e.target.checked)}
            data-testid="dry-run-toggle"
          />
          Dry run (preview only, nothing writes to butler1)
        </label>

        <textarea
          value={operatorComment}
          onChange={(e) => setOperatorComment(e.target.value)}
          placeholder="Note for the deploy log (optional)"
          rows={2}
          className="mb-3 w-full rounded-md border border-slate-200 px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
          aria-label="Deploy note"
        />

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDeploy}
            disabled={!canDeploy || busy}
            data-testid="deploy-confirm"
            className={`flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-white ${
              dryRun ? 'bg-butler-500 hover:bg-butler-600' : 'bg-marquee-600 hover:bg-marquee-700'
            } disabled:opacity-50`}
          >
            <ShieldCheck className="h-3 w-3" />
            {dryRun ? 'Run dry-run' : 'Deploy live'}
          </button>
        </div>
      </div>
    </div>
  )
}
